/**
 * Helpers for the Staff "Edit Email Address" flow.
 *
 * Kept as pure utilities so permission / SSO / account-status rules can be
 * unit-tested without mounting the full Edit Provider Details modal.
 */

/** Microsoft-first invite orgs — staff email self-serve is out of scope. */
export const SSO_INVITE_ORGS = new Set([
  'SLaM',
  '3 Borough Adult ADHD and Autism Service',
]);

/**
 * Account status label shown in the Edit Provider Details summary.
 * Active = password has been set / first successful sign-in completed.
 */
export function getStaffAccountStatus(clinician) {
  return clinician?.hasSetOwnPassword === true ? 'Active' : 'Not yet signed in';
}

export function isStaffAccountActive(clinician) {
  return clinician?.hasSetOwnPassword === true;
}

/**
 * True when self-serve email edit must be blocked because the target uses
 * Microsoft-first / SSO invite onboarding (or a linked IdP authMode).
 */
export function isStaffEmailEditSsoGated(clinician) {
  if (!clinician) return true;
  const authMode = (clinician.authMode || 'hybrid').toLowerCase();
  if (authMode === 'with_microsoft_sso') return true;
  if (SSO_INVITE_ORGS.has(clinician.organisation)) return true;
  return false;
}

/**
 * Whether the caller may edit the target staff member's email.
 *
 * Mirrors archive fine-grained rules, except self-edit is allowed:
 * - Org. Admin → any same-org provider (including other Org. Admins and self)
 * - Support → Support and Clinician only (not Org. Admin)
 */
export function canEditStaffEmail(caller, targetClinician) {
  if (!caller || !targetClinician) return false;
  if (caller.userType === 'Org. Admin') return true;
  if (caller.userType === 'Support') {
    return (
      targetClinician.userType === 'Clinician' ||
      targetClinician.userType === 'Support'
    );
  }
  return false;
}

/**
 * Email format check for staff/clinician email edit forms.
 *
 * Layers five structural guards on top of the base regex to reject addresses
 * that Firebase Auth would reject with INVALID_EMAIL — trailing dots,
 * consecutive dots, double @, etc.  Mirrors the guards in
 * validate_email_format (clinician_email_update.py) and validateEmail
 * (informantValidation.js / patientSetupValidation.js) so users see an
 * inline form error instead of a server 500.
 */
export function isValidStaffEmailFormat(email) {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (!trimmed || trimmed.length > 254) return false;
  // Reject leading/trailing dots or commas
  if (/^[.,]/.test(trimmed) || /[.,]$/.test(trimmed)) return false;
  // Reject consecutive dots anywhere
  if (trimmed.includes('..')) return false;
  // Exactly one @ required
  if ((trimmed.match(/@/g) || []).length !== 1) return false;
  // No trailing dot in local part or leading dot in domain part
  const [local, domain] = trimmed.split('@');
  if (local.endsWith('.') || domain.startsWith('.')) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/**
 * Whether the new email is valid, actually changed, and (when required) the
 * confirm field matches. The Staff Edit Email form always passes
 * requireConfirm=true so active and not-yet-signed-in accounts share the same
 * chrome; the flag remains for unit-test flexibility.
 */
export function isStaffEmailConfirmReady({
  currentEmail,
  newEmail,
  confirmEmail,
  requireConfirm,
}) {
  if (!isValidStaffEmailFormat(newEmail)) return false;
  const normalisedNew = newEmail.trim().toLowerCase();
  const normalisedCurrent = (currentEmail || '').trim().toLowerCase();
  if (normalisedNew === normalisedCurrent) return false;
  if (requireConfirm) {
    return (confirmEmail || '').trim().toLowerCase() === normalisedNew;
  }
  return true;
}

/** Shown when neither welcomeEmailFirstSentAt nor createdAt is available. */
export const STAFF_WELCOME_FIRST_SENT_FALLBACK =
  'Original email sent at account creation';

/**
 * Whether the caller may open the Resend Welcome Email action for a staff member.
 *
 * Broader than email edit: Org. Admin and Support may resend for any same-org
 * provider role, including Org. Admins. (Cross-org is enforced server-side.)
 */
export function canResendStaffWelcome(caller) {
  if (!caller) return false;
  return caller.userType === 'Org. Admin' || caller.userType === 'Support';
}

/**
 * Welcome resend is only useful before the staff member has completed setup.
 * Mirrors patient/informant hasSetOwnPassword gating.
 */
export function canResendStaffWelcomeForTarget(clinician) {
  return clinician?.hasSetOwnPassword !== true;
}

/**
 * Resolve what to show for "First sent" on the confirm-resend step.
 *
 * Priority: denormalized welcomeEmailFirstSentAt → createdAt → fallback copy.
 * No Firestore audit query — keeps the UI free of composite indexes.
 *
 * @returns {{ kind: 'timestamp', value: any } | { kind: 'fallback_copy', value: string }}
 */
export function resolveStaffWelcomeFirstSent(clinician) {
  if (clinician?.welcomeEmailFirstSentAt) {
    return { kind: 'timestamp', value: clinician.welcomeEmailFirstSentAt };
  }
  if (clinician?.createdAt) {
    return { kind: 'timestamp', value: clinician.createdAt };
  }
  return { kind: 'fallback_copy', value: STAFF_WELCOME_FIRST_SENT_FALLBACK };
}
